/**
 * PLAT-10644 — shared presentation primitives for the translation editing
 * popovers (`CompositionQuickEditPopover`, `TranslatedPhraseEditorPopover`,
 * `ReplaceSpanEditor`).
 *
 * One "translation field" pattern across all surfaces:
 *   • The editable translation is the only input-looking box (bordered,
 *     gray `fieldFocusCss` focus state) — the visual hero of the field.
 *   • Original / source text renders as a quiet flat reference
 *     (`OriginalReference`): left-rule quote, muted, never boxed like an
 *     input.
 *   • Per-value actions (Revert) are inline text actions on the row they
 *     act on (`InlineTextAction`), keeping the footer to Cancel / Save.
 *   • Section labels are sentence-styled (never uppercase) and name
 *     languages by display name ("Original · English"), never locale codes.
 */
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import React from 'react';
import styled from 'styled-components';

/**
 * Re-exported so the translation popovers keep their existing import. The one
 * definition now lives in `common/fieldStateStyles`, where every other surface
 * can reach it without importing across into `transcript/`.
 */
export { fieldFocusCss } from '@src/components/common/fieldStateStyles';

/** Muted 11px section / field label. Never uppercase (design system). */
export const SectionLabel = styled.div`
  font-family: 'Inter', sans-serif;
  font-weight: var(--font-weight-medium);
  font-size: 11px;
  line-height: 1.2;
  color: var(--popover-foreground);
  opacity: 0.65;
`;

/**
 * Flat read-only reference block for original / source text. A left-rule
 * quote — visibly NOT an input — so the editable translation below it
 * stays the only box that invites typing.
 */
export const OriginalReference = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  line-height: 1.4;
  color: var(--popover-foreground);
  opacity: 0.75;
  padding: 2px 0 2px 8px;
  border-left: 2px solid var(--border-elevated);
  white-space: pre-wrap;
  word-break: break-word;
  user-select: text;
`;

/**
 * Quiet link-style button for per-value actions (Revert). Green text per
 * the design system's action color; hover is the neutral
 * `--surface-elevated` (never a green wash). Disabled state stays visible
 * but inert.
 */
export const InlineTextAction = styled.button`
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-input);
  color: var(--primary);
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  line-height: 1.2;
  padding: 2px 8px;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
  }
  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 1px;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

/**
 * Rotating disclosure chevron: points right when collapsed, down when
 * expanded. Wraps the shared `ChevronDownIcon` so the popovers stop
 * hand-rolling unicode ▸/▾ glyphs.
 */
const DisclosureChevronSpan = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 150ms ease;
  transform: rotate(-90deg);

  &[data-expanded] {
    transform: rotate(0deg);
  }
`;

export const DisclosureChevron: React.FC<{
  expanded: boolean;
  size?: number;
}> = ({ expanded, size = 10 }) => (
  <DisclosureChevronSpan data-expanded={expanded || undefined} aria-hidden>
    <ChevronDownIcon width={`${size}`} height={`${size}`} />
  </DisclosureChevronSpan>
);

/**
 * Collapsed-by-default source-text section: a quiet "Original ▸" disclosure
 * that reveals the flat `OriginalReference` on click. Source text is
 * context, not the task — collapsing it keeps the editor compact
 * (PLAT-10644). Hover shows the text in a native title tooltip while
 * collapsed.
 */
const CollapsibleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CollapsibleToggle = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  font-family: 'Inter', sans-serif;
  font-weight: var(--font-weight-medium);
  font-size: 11px;
  line-height: 1.2;
  color: var(--popover-foreground);
  opacity: 0.65;

  &:hover {
    opacity: 1;
  }
  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 1px;
    border-radius: var(--radius-input);
  }
`;

export const CollapsibleOriginal: React.FC<{
  label: string;
  text: string;
  testId?: string;
  /** Initial expanded state (default collapsed). */
  defaultExpanded?: boolean;
}> = ({ label, text, testId, defaultExpanded = false }) => {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  return (
    <CollapsibleSection>
      <CollapsibleToggle
        type="button"
        onClick={() => setExpanded(v => !v)}
        aria-expanded={expanded}
        title={expanded ? undefined : text}
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        {label}
        <DisclosureChevron expanded={expanded} size={9} />
      </CollapsibleToggle>
      {expanded && (
        <OriginalReference data-testid={testId}>{text}</OriginalReference>
      )}
    </CollapsibleSection>
  );
};

/** Toggle-left / action-right header for a `CollapsibleValueSection`. */
const CollapsibleHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

/**
 * Collapsible value section with an optional inline header action. Like
 * `CollapsibleOriginal`, but the disclosed content is passed as children (any
 * value block) and an `action` node (e.g. a Revert `InlineTextAction`) pins to
 * the right of the toggle row — shown only while the section is expanded, since
 * the action operates on the revealed value. Collapsed by default (PLAT-10702).
 */
export const CollapsibleValueSection: React.FC<{
  label: string;
  action?: React.ReactNode;
  defaultExpanded?: boolean;
  testId?: string;
  children: React.ReactNode;
}> = ({ label, action, defaultExpanded = false, testId, children }) => {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  return (
    <CollapsibleSection>
      <CollapsibleHeaderRow>
        <CollapsibleToggle
          type="button"
          onClick={() => setExpanded(v => !v)}
          aria-expanded={expanded}
          data-testid={testId ? `${testId}-toggle` : undefined}
        >
          {label}
          <DisclosureChevron expanded={expanded} size={9} />
        </CollapsibleToggle>
        {expanded && action}
      </CollapsibleHeaderRow>
      {expanded && children}
    </CollapsibleSection>
  );
};

/** Footer action row: [contextual action] <spacer> Cancel / Save. */
export const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`;

export const Spacer = styled.div`
  flex: 1;
`;

export const BaseButton = styled.button`
  cursor: pointer;
  padding: 6px 14px;
  border-radius: var(--radius-input);
  font-family: 'Inter', sans-serif;
  font-weight: var(--font-weight-medium);
  font-size: 12px;
  line-height: 1.2;
  border: 1px solid transparent;
  transition:
    background 0.12s ease,
    border-color 0.12s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

/* Design-system Primary (docs/design-system.md § Button Hierarchy):
   solid green, semi-bold, --primary-hover on hover. */
export const PrimaryButton = styled(BaseButton)`
  background: var(--primary);
  color: var(--primary-foreground);
  border-color: var(--primary);
  font-weight: var(--font-weight-semi-bold);

  &:hover:not(:disabled) {
    background: var(--primary-hover);
    border-color: var(--primary-hover);
  }
`;

/* Design-system Secondary: neutral outline; hover is --surface-elevated +
   --text-muted border — NEVER a green tint (green is reserved for the
   primary action). */
export const SecondaryButton = styled(BaseButton)`
  background: transparent;
  color: var(--popover-foreground);
  border-color: var(--border);

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    border-color: var(--text-muted);
  }
`;
